import { auth, db } from "@infrastructure/config/firebase";
import logger from "@logger";
import { isValidEventSlug } from "@shared/utils/eventIdValidation";
import { doc, type FirestoreError, getDoc, serverTimestamp, setDoc } from "firebase/firestore";

const APPLY_POLL_MS = 250;
const APPLY_TIMEOUT_MS = 20_000;

type EnteredAtLike = { toMillis?: () => number } | Date | null | undefined;

type UserEventSnapshot = {
  lastKnownEventId?: string;
  lastKnownEventEnteredAt?: EnteredAtLike;
};

function toEnteredAtMs(value: EnteredAtLike): number | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value.toMillis === "function") {
    const ms = value.toMillis();
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

async function readUserEventSnapshot(userId: string): Promise<UserEventSnapshot | undefined> {
  const userRef = doc(db, "users", userId);
  try {
    const snapshot = await getDoc(userRef);
    return snapshot.data() as UserEventSnapshot | undefined;
  } catch (error) {
    const firestoreError = toFirestoreError(error);
    if (firestoreError?.code === "permission-denied") {
      throw new Error("Could not read user event context (permission denied). Deploy Firestore rules for users.");
    }
    throw error;
  }
}

/**
 * Waits until the user document reflects the requested event AND its join time
 * has advanced past `baselineEnteredAtMs`. This handles same-event re-scans,
 * where `lastKnownEventId` already matches and only the timestamp changes.
 */
async function waitForUserEventContext(
  userId: string,
  eventId: string,
  baselineEnteredAtMs: number | null
): Promise<void> {
  const deadline = Date.now() + APPLY_TIMEOUT_MS;

  while (Date.now() < deadline) {
    const userData = await readUserEventSnapshot(userId);
    if (userData?.lastKnownEventId === eventId) {
      const enteredAtMs = toEnteredAtMs(userData.lastKnownEventEnteredAt);
      const advanced = enteredAtMs != null && (baselineEnteredAtMs == null || enteredAtMs > baselineEnteredAtMs);
      if (advanced) {
        return;
      }
    }
    await new Promise((resolve) => {
      setTimeout(resolve, APPLY_POLL_MS);
    });
  }

  throw new Error(
    "Timed out waiting for event context to apply. Deploy Cloud Functions (onEventContextRequest) and Firestore rules, and confirm the event exists in admin."
  );
}

function toFirestoreError(error: unknown): FirestoreError | null {
  if (typeof error === "object" && error !== null && "code" in error) {
    return error as FirestoreError;
  }
  return null;
}

export async function setCurrentEventContext(eventId: string): Promise<void> {
  const trimmedEventId = eventId.trim();
  if (!isValidEventSlug(trimmedEventId)) {
    throw new Error("Invalid eventId");
  }

  const userId = auth.currentUser?.uid;
  if (!userId) {
    throw new Error("Must be signed in");
  }

  logger.info("Requesting event context via Firestore", { userId, eventId: trimmedEventId });

  const baseline = await readUserEventSnapshot(userId);
  const baselineEnteredAtMs = toEnteredAtMs(baseline?.lastKnownEventEnteredAt);

  try {
    await setDoc(
      doc(db, "eventContextRequests", userId),
      {
        eventId: trimmedEventId,
        requestedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    const firestoreError = toFirestoreError(error);
    if (firestoreError?.code === "permission-denied") {
      throw new Error(
        "Could not save event context (permission denied). Deploy Firestore rules for eventContextRequests."
      );
    }
    throw error;
  }

  try {
    await waitForUserEventContext(userId, trimmedEventId, baselineEnteredAtMs);
  } catch (error) {
    logger.error("Failed to apply event context via Firestore request", {
      userId,
      eventId: trimmedEventId,
      error,
    });
    throw error;
  }
}
